import { FormProvider, useForm } from '@/components/react-hook-form';
import { PrimaryClientDeathEvent } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { ContextPrimaryClient } from '@/modules/household/contexts/householdDetails.context';
import { TransferMethod } from '@/types/schema';

import {
  UseRecipientOptionsParams,
  useRecipientOptionsParams,
} from '../hooks/useRecipientOptions';
import { useRemainingExemptionAmountForClient } from '../hooks/useRemainingExemptionAmountForClient';
import { useDeathOrderAwareClient } from '../hooks/utilityHooks';
import { SplitAssetSelector } from './SplitAssetSelector';
import {
  SplitDispositionFormShape,
  SplitDispositionModalResult,
} from './SplitDispositionModal.types';
import { SplitModalShell } from './SplitModalShell';
import { SplitRecipientsSelector } from './SplitRecipientsSelector';

interface SplitDispositionModalProps {
  isOpen: boolean;
  onClose: (result: SplitDispositionModalResult | null) => void;
  hasStateTax?: boolean;
  deathEvent: PrimaryClientDeathEvent;
}

const DEFAULT_FORM_VALUES: SplitDispositionFormShape = {
  distributionKind: 'percentage',
  distributionValue: null,
  formulaKind: '',
  maxDistributionAmount: null,
  selectedRecipients: [],
  assetGroupIds: [],
  assetStaticIds: [],
  insurancePolicyIds: [],
  ownershipStakeIds: [],
  dispositionAssetPercentage: null,
  transferMethod: TransferMethod.InKind,
};

export function SplitDispositionModal({
  isOpen,
  onClose,
  hasStateTax = false,
  deathEvent,
}: SplitDispositionModalProps) {
  // Call hooks that depend on the parent V2 form context before FormProvider
  // shadows it with our split disposition form context
  const firstDeadGrantor = useDeathOrderAwareClient({ deathEvent });
  const recipientOptionsParams = useRecipientOptionsParams({ deathEvent });

  const formMethods = useForm<SplitDispositionFormShape>({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  return (
    <FormProvider {...formMethods}>
      <SplitDispositionModalInner
        isOpen={isOpen}
        onClose={onClose}
        hasStateTax={hasStateTax}
        firstDeadGrantor={firstDeadGrantor}
        recipientOptionsParams={recipientOptionsParams}
      />
    </FormProvider>
  );
}

interface SplitDispositionModalInnerProps {
  isOpen: boolean;
  onClose: (result: SplitDispositionModalResult | null) => void;
  hasStateTax?: boolean;
  firstDeadGrantor: ContextPrimaryClient;
  recipientOptionsParams: UseRecipientOptionsParams;
}

function SplitDispositionModalInner({
  isOpen,
  onClose,
  hasStateTax = false,
  firstDeadGrantor,
  recipientOptionsParams,
}: SplitDispositionModalInnerProps) {
  const { data: clientExemptionData } = useRemainingExemptionAmountForClient({
    clientId: firstDeadGrantor?.id ?? null,
  });

  return (
    <SplitModalShell
      isOpen={isOpen}
      onClose={onClose}
      hasStateTax={hasStateTax}
      heading="Create evenly split disposition"
      clientExemptionData={clientExemptionData}
      renderAssetSelector={() => <SplitAssetSelector />}
      renderRecipientSelector={(errorMessage) => (
        <SplitRecipientsSelector
          errorMessage={errorMessage}
          hasStateTax={hasStateTax}
          recipientOptionsParams={recipientOptionsParams}
        />
      )}
    />
  );
}
